import { fetchAuthApi, getAuthApiUrl } from './client';
import { getRefreshToken } from '@/lib/tokenStorage';
import type { TokenResponse } from '@/types/api';

interface UserMeResponse {
  id: string;
  name: string;
  email: string;
  role_id?: string;
  role_name?: string;
  status?: 'active' | 'inactive';
  last_login_at?: string;
  created_at: string;
  updated_at: string;
  role?: {
    id: string;
    name: string;
    description?: string;
    created_at: string;
    updated_at: string;
  };
}

/**
 * Phase 4: 모든 auth 흐름은 ontoh-auth-api (`VITE_AUTH_API_URL`).
 * 로그인은 `identifier` 키로 email 또는 person_id 를 받음 (auth-api 계약).
 */
export async function login(
  email: string,
  password: string
): Promise<TokenResponse> {
  const response = await fetch(`${getAuthApiUrl()}/v1/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ identifier: email, password }),
  });

  if (!response.ok) {
    let errorMessage = 'Login failed';
    try {
      const errorData = await response.json();
      if (errorData.detail) {
        errorMessage =
          typeof errorData.detail === 'string'
            ? errorData.detail
            : errorData.detail.code ?? JSON.stringify(errorData.detail);
      }
    } catch {
      // ignore
    }
    throw new Error(errorMessage);
  }

  return response.json();
}

export async function getMe(): Promise<UserMeResponse> {
  return fetchAuthApi<UserMeResponse>('/v1/auth/me', { method: 'GET' });
}

export async function logout(): Promise<void> {
  try {
    const refreshToken = getRefreshToken();
    await fetchAuthApi<void>('/v1/auth/logout', {
      method: 'POST',
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
  } catch {
    // 클라이언트 토큰 정리는 호출자(useAuthStore)가 어차피 진행
  }
}

export async function forgotPassword(email: string): Promise<{ message: string }> {
  return fetchAuthApi<{ message: string }>('/v1/auth/forgot-password', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

export async function resetPassword(
  token: string,
  newPassword: string,
): Promise<{ message: string }> {
  return fetchAuthApi<{ message: string }>('/v1/auth/reset-password', {
    method: 'POST',
    body: JSON.stringify({ token, new_password: newPassword }),
  });
}

export async function changePassword(
  currentPassword: string,
  newPassword: string,
): Promise<{ message: string }> {
  return fetchAuthApi<{ message: string }>('/v1/auth/me/password', {
    method: 'PUT',
    body: JSON.stringify({
      current_password: currentPassword,
      new_password: newPassword,
    }),
  });
}

export async function updateMe(data: {
  name?: string;
  phone?: string;
  email?: string; // auth-api 는 email 변경 미지원 — 보내도 무시·또는 에러. UI 호환용.
}): Promise<UserMeResponse> {
  // email 은 auth-api 에서 identity 라 PUT /me 로 변경 불가. 전송 페이로드에서 제거.
  const payload: { name?: string; phone?: string } = {};
  if (data.name !== undefined) payload.name = data.name;
  if (data.phone !== undefined) payload.phone = data.phone;
  return fetchAuthApi<UserMeResponse>('/v1/auth/me', {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
}
